"use client";

import React from "react";

import { ProfileFilterChip } from "./ProfileFilterChip";

export const PROFILE_FILTER_ALL_VALUE = "all";

export type ProfileFilterChipOption<T extends string = string> = Readonly<{
  value: T;
  label: React.ReactNode;
  componentInstance?: string;
}>;

export type ProfileFilterChipGroupProps<T extends string = string> = Readonly<{
  options: readonly ProfileFilterChipOption<T>[];
  activeValue: T;
  onSelect: (value: T) => void;
  ariaLabel?: string;
  className?: string;
  variant?: "toolbar" | "tab";
  /**
   * The always-present "All" chip, rendered first. Enabled by default so every
   * filter view exposes "All" without each caller re-implementing it. Pass
   * `false` to omit it, or override its value/label. If `options` already
   * contains the resolved all value, it is not duplicated.
   */
  allOption?: false | Readonly<{ value: T; label?: React.ReactNode; componentInstance?: string }>;
}>;

/**
 * Shared filter toolbar/tablist built on {@link ProfileFilterChip}. Owns the
 * "All" default so all views follow the same pattern and none can silently
 * drop the "All" option.
 */
export function ProfileFilterChipGroup<T extends string = string>({
  options,
  activeValue,
  onSelect,
  ariaLabel,
  className,
  variant = "toolbar",
  allOption,
}: ProfileFilterChipGroupProps<T>) {
  const resolvedAll: ProfileFilterChipOption<T> | null = allOption === false
    ? null
    : {
        value: allOption?.value ?? (PROFILE_FILTER_ALL_VALUE as T),
        label: allOption?.label ?? "All",
        componentInstance: allOption?.componentInstance,
      };

  const chips: ProfileFilterChipOption<T>[] = [];
  const seen = new Set<T>();
  if (resolvedAll) {
    chips.push(resolvedAll);
    seen.add(resolvedAll.value);
  }
  for (const option of options) {
    if (seen.has(option.value)) {
      continue;
    }
    seen.add(option.value);
    chips.push(option);
  }

  return (
    <div
      className={["profile-filter-chip-group", className].filter(Boolean).join(" ")}
      role={variant === "tab" ? "tablist" : "toolbar"}
      {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
    >
      {chips.map((chip) => (
        <ProfileFilterChip
          key={chip.value}
          variant={variant}
          isActive={chip.value === activeValue}
          label={chip.label}
          componentInstance={chip.componentInstance}
          onClick={() => onSelect(chip.value)}
        />
      ))}
    </div>
  );
}
